import React, { useState } from 'react';
import { LayoutGrid, Moon, Zap, Dumbbell, User, Pill, Utensils } from 'lucide-react';
import { Box, Typography } from '@/shared/design-system/primitives';
import { useUIStore } from '@stores/v4/useUIStore';

interface MissionBoardHeaderProps {
    onExit: () => void;
    onToggleSleep: () => void;
    onLogActivity: (date: Date) => void;
    onNavigate: (view: string | null) => void;
    onQuickMetabolic: () => void;
    onQuickNutrition: () => void;
    onQuickSupplement: (window?: string, scheduledTime?: Date) => void;
    selectedDate: Date;
    activeRecoverySession: any;
}

/**
 * MissionBoardHeader: Tactical header with quick-action telemetry.
 * REFINED: Middle-ground sizing for Galaxy A41 stability.
 * FIXED: Vertical alignment of toggles and icon proportions.
 */
export const MissionBoardHeader: React.FC<MissionBoardHeaderProps> = ({
    onExit, onToggleSleep, onLogActivity, onNavigate, 
    onQuickMetabolic, onQuickNutrition, onQuickSupplement,
    selectedDate, activeRecoverySession
}) => {
    const { 
        calendarViewMode, setCalendarViewMode, 
        agendaViewMode, setAgendaViewMode, 
        isMetabolicViewActive
    } = useUIStore();

    // MIDDLE-GROUND CONFIGURATION
    const HEADER_HEIGHT = 62;
    const ICON_SIZE = 21; 

    return (
        <header style={{ 
            display: 'flex', 
            flexDirection: 'row',
            alignItems: 'center', 
            height: `${HEADER_HEIGHT}px`, 
            padding: '0 8px', 
            zIndex: 100, 
            position: 'relative', 
            flexShrink: 0, 
            borderBottom: '1px solid #222', 
            backgroundColor: '#000',
            justifyContent: 'space-between'
        }}>
            {/* 1. LEFT: NAV & TOGGLES */}
            <Box style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
                <button onClick={onExit} style={{ all: 'unset', cursor: 'pointer', padding: '8px 4px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <LayoutGrid size={ICON_SIZE} color="#f29b11" />
                </button>

                {/* Calendar Toggle */}
                <Box style={{ backgroundColor: '#0a0a0a', borderRadius: '8px', padding: '3px', height: '34px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <button 
                        onClick={() => setCalendarViewMode(calendarViewMode === 'date' ? 'month' : 'date')}
                        style={{ 
                            all: 'unset', cursor: 'pointer', paddingHorizontal: 12, borderRadius: '6px', 
                            backgroundColor: '#f29b11', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', minWidth: '45px'
                        }}
                    >
                        <Typography style={{ fontSize: 9, fontWeight: '950', color: '#000', letterSpacing: 0.2, fontVariantNumeric: 'lining-nums', textAlign: 'center', lineHeight: 1 } as any}>
                            {calendarViewMode === 'date' ? `${selectedDate.toLocaleDateString('en-US', { month: 'short' }).toUpperCase()} ${selectedDate.getDate().toString().padStart(2, '0')}` : 'MONTH'}
                        </Typography>
                    </button>
                </Box>

                {/* View Toggle */}
                <Box style={{ backgroundColor: '#0a0a0a', borderRadius: '8px', padding: '3px', height: '34px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <button 
                        onClick={() => setAgendaViewMode(agendaViewMode === 'agenda' ? 'timeline' : 'agenda')}
                        style={{ 
                            all: 'unset', cursor: 'pointer', paddingHorizontal: 12, borderRadius: '6px', 
                            backgroundColor: '#f29b11', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', minWidth: '45px'
                        }}
                    >
                        <Typography style={{ fontSize: 9, fontWeight: '950', textAlign: 'center', color: '#000', textTransform: 'uppercase', lineHeight: 1 }}>
                            {agendaViewMode === 'agenda' ? 'AGND' : 'TIME'}
                        </Typography>
                    </button>
                </Box>
            </Box>

            {/* 2. RIGHT: TOOLS, ACTIONS & PROFILE */}
            <Box style={{ flexDirection: 'row', alignItems: 'center', gap: 1 }}>
                {/* Metabolic Trigger */}
                <button onClick={onQuickMetabolic} style={{ all: 'unset', cursor: 'pointer', padding: '8px 4px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Zap size={ICON_SIZE} color={isMetabolicViewActive ? "#fff" : "#06b6d4"} />
                </button>

                {/* Supplement Trigger */}
                <button 
                    onClick={() => onQuickSupplement()} 
                    style={{ all: 'unset', cursor: 'pointer', padding: '8px 4px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                >
                    <Pill size={ICON_SIZE} color="#a855f7" />
                </button>

                {/* Nutrition Trigger */}
                <button onClick={onQuickNutrition} style={{ all: 'unset', cursor: 'pointer', padding: '8px 4px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Utensils size={ICON_SIZE} color="#2ecc71" />
                </button>

                {/* Sleep Trigger */}
                <button onClick={onToggleSleep} style={{ all: 'unset', cursor: 'pointer', padding: '8px 4px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Moon size={ICON_SIZE} color="#3b82f6" fill={activeRecoverySession ? '#3b82f6' : 'transparent'} />
                </button>

                {/* Performance Trigger */}
                <button onClick={() => onLogActivity(new Date())} style={{ all: 'unset', cursor: 'pointer', padding: '8px 4px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Dumbbell size={ICON_SIZE} color="#f29b11" strokeWidth={2.5} />
                </button>

                {/* Profile Trigger */}
                <button onClick={() => onNavigate('profile')} style={{ all: 'unset', cursor: 'pointer', padding: '8px 4px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <User size={ICON_SIZE} color="#666" />
                </button>
            </Box>
        </header>
    );
};
